import { type DataProvider } from 'react-admin';
import jsonServerProvider from 'ra-data-json-server';
import axiosInstance from './axiosConfig.ts';
import * as Sentry from '@sentry/react';

const axiosHttpClient = async (url: string, options: any = {}) => {
  const { method = 'GET', body } = options;

  try {
    const response = await axiosInstance({
      url,
      method,
      data: body
    });

    const headers = new Headers();

    Object.keys(response.headers).forEach(key => {
      headers.set(key, response.headers[key]);
    });

    if (response.headers['x-total-count']) {
      headers.set('X-Total-Count', response.headers['x-total-count']);
    }

    return {
      status: response.status,
      headers: headers,
      json: response.data,
    };
  } catch (error: any) {
    Sentry.captureException(error);
    return Promise.reject({
      status: error.response?.status || 500,
      message: error.response?.data?.message || error.message,
      body: error.response?.data,
    });
  }
};

const apiUrl = process.env.REACT_APP_API_URL!;
const base = jsonServerProvider(apiUrl, axiosHttpClient);

const map: Record<string, Partial<Record<keyof DataProvider, string>>> = {
  members: {
    getList: 'admin/member/list',
    getOne: 'admin/members',
    getMany: 'admin/members',
    create: 'admin/member/add',
    update: 'admin/members',
    delete: 'admin/members',
  },
  commissionManagement: {
    getList: 'admin/agency/list',
  },
  locationsManagement: {
    getList: 'location/list',
  },
  riderManagement: {
    getList: 'admin/courier/management/list',
  },
  commissionCouriers: {
    getList: 'admin/courier/management/list',
  },
  locationItems: {
    getList: '/restaurant/get/admin',
  },
  riderPerformance: {
    getList: 'admin/courier/management/list',
  },
  itemsManagement: {
    getList: 'menu-item/get/list',
  },
  categories: {
    getList: 'category/list',
  },
};

//TODO: import language from i18n

export const dataProvider: DataProvider = {
  ...base,
  getList: (resource, params) => {
    const newFilter = { ...params.filter, language: "ENG" };

    return base.getList(map[resource]?.getList || resource, {
      ...params,
      filter: newFilter
    });
  },
  getOne: async (resource, params) => {
    if (resource === 'members') {
      const { total } = await base.getList('admin/member/list', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: { language: 'ENG' },
      });
      const res = await base.getList('admin/member/list', {
        pagination: { page: 1, perPage: total || 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: { language: 'ENG' },
      });
      const record = res.data.find((r: any) => String(r.id) === String(params.id));
      if (!record) return Promise.reject({ message: 'Member not found' });
      return { data: record };
    }
    return base.getOne(map[resource]?.getOne || resource, params);
  },
  getMany: (resource, params) =>
    base.getMany(map[resource]?.getMany || resource, params),
  create: (resource, params) =>
    base.create(map[resource]?.create || resource, params),
  update: (resource, params) =>
    base.update(map[resource]?.update || resource, params),
  delete: (resource, params) =>
    base.delete(map[resource]?.delete || resource, params),
  getManyReference: (resource, params) =>
    base.getManyReference(map[resource]?.getManyReference || resource, params),
};

export default dataProvider;